import { RadioGroup } from "@headlessui/react";
import { classNames, navigateTo } from "../../helper";
import { RootState } from "../../store/root-reducer";
import { connect, useDispatch } from "react-redux";
import { FppPlan, PlanStatus } from "../../types/plan";
import { Controller, useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { useEffect, useState } from "react";
import RouteNames from "../../routes/type";
import { useTranslation } from "react-i18next";
import { PlanCardField } from "../../components/PlanCard";

interface Props {
  plans: FppPlan[];
}

const DATE_FORMAT = "MMMM Do, YYYY";

function SelectPlan({ plans }: Props) {
  const { control, setValue, clearErrors } = useFormContext();
  const [searchParams] = useSearchParams();
  const warranty_id: string = searchParams.get("warranty_id") || null;
  const dispatch = useDispatch();
  useEffect(() => {
    const activePlans = plans.filter(
      (plan) => plan.planStatus === PlanStatus.ACTIVE
    );
    const selectedWarrantyId =
      activePlans.length === 1
        ? activePlans[0].id
        : warranty_id
          ? warranty_id
          : null;
    if (selectedWarrantyId) {
      setValue("selectedPlan", selectedWarrantyId, {
        shouldValidate: true,
      });
      clearErrors("selectedPlan");
    }
    if (plans.length <= 0) {
      navigateTo(RouteNames.NO_WARRANTY_FOUND);
    }
    return () => {};
  }, [clearErrors, dispatch, plans, setValue, warranty_id]);

  return (
    <>
      <Controller
        name={"selectedPlan"}
        key={"selectedPlan"}
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { value, onChange } }) => (
          <RadioGroup value={value} onChange={onChange}>
            <div className="space-y-3 rounded-xl bg-white">
              {plans.map((plan) => (
                <RadioGroup.Option
                  key={plan.id}
                  value={plan.id}
                  disabled={plan.planStatus !== PlanStatus.ACTIVE}
                  className={({ checked }) =>
                    classNames(
                      checked
                        ? "bg-blue-light border-brand-color z-10"
                        : "border-border-gray",
                      "relative border overflow-hidden cursor-pointer outline-none transition-all w-full select-none rounded-xl"
                    )
                  }
                >
                  {({ checked, disabled }) => (
                    <>
                      <div
                        className={classNames(
                          "flex px-2 py-2 justify-start flex-row items-start gap-1 bg-[#2D6CCA] bg-opacity-10 w-full",
                          disabled && "opacity-65 cursor-not-allowed"
                        )}
                      >
                        <div
                          className={classNames(
                            checked
                              ? "bg-brand-color border-transparent"
                              : "bg-white border-gray-300",
                            "h-4 mt-2 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center transition-all"
                          )}
                          aria-hidden="true"
                        >
                          <span className="rounded-full bg-white w-1.5 h-1.5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#2D6CCA]">
                            {plan.programName}
                          </h3>
                          {plan.expirationDate && (
                            <p className="text-black text-sm font-normal">
                              Active until{" "}
                              {moment(plan.expirationDate).format(DATE_FORMAT)}
                            </p>
                          )}
                        </div>
                        <p
                          className={classNames(
                            "ml-auto text-white rounded-full  px-2 text-sm",
                            plan.planStatus === PlanStatus.ACTIVE &&
                              "bg-blue-500",
                            plan.planStatus === PlanStatus.CANCELLED &&
                              "bg-red-400",
                            plan.planStatus === PlanStatus.Expired &&
                              "bg-gray-400",
                            [
                              PlanStatus.CancelCustomerReview,
                              PlanStatus.CancelDealerReview,
                            ].includes(plan.planStatus) && "bg-yellow-400"
                          )}
                        >
                          {plan.planStatus}
                        </p>
                      </div>
                      <div
                        className={classNames(
                          "bg-white grid gap-1.5 divide-y-[1px] divide-[#F0F0F0] px-2 py-2",
                          disabled && "opacity-65 cursor-not-allowed"
                        )}
                      >
                        <PlanCardField
                          label={"Customer Name"}
                          value={plan.customerName}
                        />
                        <PlanCardField
                          label={"Purchase Date"}
                          value={plan.purchaseDate}
                          date
                        />
                        <PlanCardField
                          label={"Delivery Date"}
                          value={plan.deliveryDate}
                          date
                        />
                        <PlanCardField
                          label={"Total Purchase Price"}
                          value={plan.totalPrice}
                          price
                        />
                        <PlanCardField
                          label={"Store Code"}
                          value={plan.store}
                        />
                        <PlanCardField
                          label={"Invoice"}
                          value={plan.invoiceId}
                        />
                      </div>
                      <WarrantyItemsTable items={plan.warrantyItems || []} />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        )}
      />
    </>
  );
}

function WarrantyItemsTable({ items }) {
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();

  const visibleItems = showAll ? items : items.slice(0, 3);

  return (
    <>
      <p className="text-sm text-blue-800 p-2">
        {t(
          "If you do not see your item listed below, you can still add the item, but we will require a copy of your invoice/sales receipt to proceed with your claim."
        )}
      </p>
      <div className="bg-white overflow-x-auto">
        <table className="min-w-full text-left text-sm border rounded-lg ">
          <thead className="bg-gray-100 font-medium">
            <tr>
              <th className="p-2">{t("Item type")}</th>
              <th className="p-2">{t("Model")}</th>
              <th className="p-2">{t("SKU")}</th>
              <th className="p-2">{t("Vendor")}</th>
              <th className="p-2">{t("Material")}</th>
              <th className="p-2">{t("Price")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item, index) => (
              <tr key={index} className="border-t">
                <td className="p-2">
                  {item.name || item.itemDescription || "N/A"}
                </td>
                <td className="p-2">{item.modelNumber || "N/A"}</td>
                <td className="p-2">{item.sku || "N/A"}</td>
                <td className="p-2">{item.vendor || "N/A"}</td>
                <td className="p-2">{item.material?.name || "N/A"}</td>
                <td className="p-2">
                  {item.itemPrice ? `$${item.itemPrice.toFixed(2)}` : "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length > 3 && (
        <div className="px-2 py-1 text-right ">
          <button
            onClick={() => setShowAll(!showAll)}
            type="button"
            className="text-blue-600 hover:underline text-sm"
          >
            {showAll ? t("Show Less") : t("Show More")}
          </button>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  plans: state?.user?.plans || [],
});

export default connect(mapStateToProps)(SelectPlan);
